import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  CircleSlash,
  Clock,
  Cloud,
  FolderX,
  GitFork,
  GitMerge,
  GitPullRequest,
  GitPullRequestClosed,
  GitPullRequestDraft,
  LoaderCircle,
  Pin,
  ShieldAlert,
  X,
} from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLongPress } from '@/components/mobile-shell/use-long-press';
import {
  formatAge,
  type SidebarRow,
  type SidebarRowBadge,
  type SidebarRowOutcome,
  type SidebarRowState,
} from '@/lib/desktop-shell/sidebar-model';
import { formatSidebarUpdatedAt, type SidebarThread } from '@/lib/sidebar-threads';
import {
  ConversationActionsButton,
  ConversationContextMenu,
  type RowMenuAction,
  useConversationActions,
} from './conversation-actions';
import { useShellNavigation } from './shell-navigation';

const STATE_LABEL: Record<SidebarRowState, string> = {
  running: 'Running',
  approval: 'Waits on you',
  question: 'Asks you a question',
  idle: 'Idle',
};

const OUTCOME_LABEL: Record<SidebarRowOutcome, string> = {
  done: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

/**
 * What a conversation is doing, as its row's leading icon (D1), its state only: a blue ring
 * while it runs, an amber shield or question mark while it waits on you, a grey dot otherwise;
 * a task or a step that ended, a check, a red cross or a slash. What it is (a PR's, an
 * automation's run) is its {@link RowBadge}.
 */
export function RowStateIcon({
  state,
  outcome,
  className,
}: {
  state: SidebarRowState;
  outcome?: SidebarRowOutcome | undefined;
  className?: string;
}) {
  const ended = state === 'idle' ? outcome : undefined;
  const label = ended === undefined ? STATE_LABEL[state] : OUTCOME_LABEL[ended];
  const box = cn('flex size-4 shrink-0 items-center justify-center', className);
  return (
    <span
      aria-label={label}
      className={box}
      data-row-outcome={ended}
      data-row-state={state}
      role="img"
    >
      {ended === 'done' ? (
        <Check className="size-3.5 text-success" />
      ) : ended === 'failed' ? (
        <X className="size-3.5 text-destructive" />
      ) : ended === 'stopped' ? (
        <CircleSlash className="size-3 text-subtle-foreground" />
      ) : state === 'running' ? (
        <LoaderCircle className="size-3.5 animate-spin text-running" />
      ) : state === 'approval' ? (
        <ShieldAlert className="size-3.5 text-attention" />
      ) : state === 'question' ? (
        <CircleHelp className="size-3.5 text-attention" />
      ) : (
        <span className="size-1.5 rounded-full bg-subtle-foreground/60" />
      )}
    </span>
  );
}

const BADGE_LABEL: Record<SidebarRowBadge, string> = {
  automation: 'Automation run',
};

/** What a conversation is, small beside its title: an automation's run. */
function RowBadge({ badge }: { badge: SidebarRowBadge }) {
  return (
    <Clock
      aria-label={BADGE_LABEL[badge]}
      className="size-3 shrink-0 text-subtle-foreground"
      data-row-badge={badge}
      role="img"
    />
  );
}

/** Where a conversation runs when it is not this device's folder, as its tooltip says it. */
export function rowPlaceLabel(thread: SidebarThread): string | null {
  if (thread.runtime === 'cloud') return 'Runs on Crowdex Cloud';
  if (thread.runtime !== 'worktree') return null;
  const name =
    thread.branch?.trim() || thread.pullRequest?.headBranch.trim() || thread.worktreeName;
  return name ? `Runs in a worktree · ${name}` : 'Runs in a worktree';
}

/** A grey cloud or worktree glyph beside the title; nothing for a conversation of this folder. */
function RowPlace({ thread }: { thread: SidebarThread }) {
  const label = rowPlaceLabel(thread);
  if (label === null) return null;
  const Icon = thread.runtime === 'cloud' ? Cloud : GitFork;
  return (
    <span
      className="flex shrink-0 items-center text-subtle-foreground"
      data-row-place={thread.runtime}
      title={label}
    >
      <Icon aria-label={label} className="size-3" role="img" />
    </span>
  );
}

/** Whether a pull request needs fixing: its checks fail, or it conflicts with its base. */
function pullRequestBroken(pullRequest: PullRequestSummary): 'checks' | 'conflicts' | null {
  if (pullRequest.state === 'merged' || pullRequest.state === 'closed') return null;
  if (pullRequest.checks === 'failing') return 'checks';
  if (pullRequest.mergeability === 'conflicting') return 'conflicts';
  return null;
}

/** "Pull request #556 · open · checks failing", its marker's name and tooltip. */
export function pullRequestMarkerLabel(pullRequest: PullRequestSummary): string {
  const parts = [`Pull request #${pullRequest.number}`, pullRequest.state];
  const broken = pullRequestBroken(pullRequest);
  if (broken === 'checks') parts.push('checks failing');
  else if (broken === 'conflicts') parts.push('merge conflicts');
  else if (pullRequest.state === 'open' || pullRequest.state === 'draft') {
    if (pullRequest.checks === 'pending') parts.push('checks running');
    if (pullRequest.checks === 'passing') parts.push('checks passed');
  }
  return parts.join(' · ');
}

/**
 * A conversation's pull request beside its title: its state's glyph (green while open, grey
 * once draft, merged or closed) and its number; red when its checks fail or it conflicts.
 */
function RowPullRequest({ pullRequest }: { pullRequest: PullRequestSummary }) {
  const broken = pullRequestBroken(pullRequest);
  const Icon =
    pullRequest.state === 'merged'
      ? GitMerge
      : pullRequest.state === 'closed'
        ? GitPullRequestClosed
        : pullRequest.state === 'draft'
          ? GitPullRequestDraft
          : GitPullRequest;
  const label = pullRequestMarkerLabel(pullRequest);
  return (
    <span
      aria-label={label}
      className={cn(
        'type-caption flex shrink-0 items-center gap-0.5 font-normal tabular-nums',
        broken !== null
          ? 'text-destructive'
          : pullRequest.state === 'open'
            ? 'text-success'
            : 'text-subtle-foreground',
      )}
      data-row-pull-request={pullRequest.state}
      data-row-pull-request-broken={broken ?? undefined}
      role="img"
      title={label}
    >
      <Icon aria-hidden className="size-3" />
      <span aria-hidden className={broken === null ? 'text-subtle-foreground' : undefined}>
        #{pullRequest.number}
      </span>
    </span>
  );
}

/** A project's colour, as a small rounded square (its data, not a theme colour). */
export function ProjectDot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('size-2.5 shrink-0 rounded-[3px]', className)}
      style={{ backgroundColor: color }}
    />
  );
}

type Density = 'desktop' | 'drawer';

const ROW = {
  desktop: 'h-[30px] gap-2.5 rounded-lg px-2.5 type-ui',
  // A finger's target: 44 px at least (mobile 2).
  drawer: 'min-h-11 gap-3 rounded-xl px-2.5 text-base',
} as const;

/**
 * A conversation of the sidebar or the drawer: its state icon, its title and the count of its
 * runs or children ("· 2"); the age only while it waits on you, else on hover. Right-click (or
 * F2, ⌘⌫, ⌥↵ on the focused row) acts on it; dragged, it splits or moves to a project.
 */
export function ConversationRow({
  row,
  depth = 0,
  density = 'desktop',
  bold = false,
  trailing,
  onLongPress,
  onToggleChildren,
  parentTitle,
  pinned = false,
  onMovePinned,
}: {
  row: SidebarRow;
  /** 1 under a project or a parent, 2 under a parent inside a project. */
  depth?: 0 | 1 | 2;
  density?: Density;
  /** Needs you rows stand out. */
  bold?: boolean;
  trailing?: ReactNode;
  /** A phone shows its actions in a sheet instead of a context menu. */
  onLongPress?: (row: SidebarRow) => void;
  /** A parent's chevron folds or unfolds its child conversations (D49). */
  onToggleChildren?: () => void;
  /** Under its parent, a child does not repeat the parent's title ("· Oct 18"). */
  parentTitle?: string;
  /** In Pinned (D22): a pin where nothing else (unread, age) shows at its end. */
  pinned?: boolean;
  /** In Pinned, ⌥↑ / ⌥↓ move it up or down, as dragging it does. */
  onMovePinned?: (delta: -1 | 1) => void;
}) {
  const navigation = useShellNavigation();
  const { thread } = row;
  const [renaming, setRenaming] = useState(false);
  const { actions, confirmDelete } = useConversationActions(thread, {
    running: row.state === 'running',
    surface: density === 'desktop' ? 'desktop' : 'phone',
    onRename: () => setRenaming(true),
  });
  const press = useLongPress(() => onLongPress?.(row));
  const active = thread.id === navigation.activeThreadId;
  const run = (id: string) => {
    const action = actions.find((entry) => entry.id === id);
    action?.run?.();
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    // The menu key, or ⇧F10: the row's actions, as a right-click shows them.
    if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
      event.preventDefault();
      if (onLongPress !== undefined) onLongPress(row);
      else openContextMenu(event.currentTarget);
      return;
    }
    if (event.key === 'F2') {
      event.preventDefault();
      run('rename');
    } else if (event.key === 'Backspace' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      run('archive');
    } else if (event.key === 'Enter' && event.altKey) {
      event.preventDefault();
      run('split-view');
    } else if (
      onMovePinned !== undefined &&
      event.altKey &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown')
    ) {
      event.preventDefault();
      onMovePinned(event.key === 'ArrowUp' ? -1 : 1);
    }
  };
  const age = row.waitingSince === null ? null : formatAge(row.waitingSince);
  // A pointer that does not right-click, and the keyboard, reach the actions from a "⋯".
  const moreButton = density === 'desktop' && onLongPress === undefined;
  const foldable = onToggleChildren !== undefined && (row.children?.length ?? 0) > 0;
  // What its steps ask, shown on it while they are folded away.
  const folded = row.expanded !== true;
  const stepsWaiting = folded && row.stepsAttention?.waiting === true && row.waitingSince === null;
  const stepsUnread = folded && row.stepsAttention?.unread === true;
  const content = renaming ? (
    <RenameField
      density={density}
      initial={thread.title}
      onDone={(title) => {
        setRenaming(false);
        if (title !== null && title !== thread.title)
          void navigation.renameConversation?.(thread, title);
      }}
    />
  ) : (
    <button
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group/row flex w-full min-w-0 items-center text-left text-foreground/90 data-[unread]:font-semibold data-[unread]:text-foreground transition-colors hover:bg-accent/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ROW[density],
        depth === 1 && (density === 'desktop' ? 'pl-[42px]' : 'pl-10'),
        depth === 2 && (density === 'desktop' ? 'pl-[62px]' : 'pl-16'),
        foldable && (density === 'desktop' ? 'pr-8' : 'pr-11'),
        // Without a hover (a tablet's touch), its "⋯" always shows: its room is kept.
        moreButton &&
          (foldable
            ? 'group-focus-within/item:pr-14 group-hover/item:pr-14 [@media(hover:none)]:pr-14'
            : 'group-focus-within/item:pr-8 group-hover/item:pr-8 [@media(hover:none)]:pr-8'),
        active && 'bg-sidebar-accent text-sidebar-accent-foreground',
      )}
      data-conversation-row={thread.id}
      data-unread={thread.unread === true || undefined}
      data-sidebar-item
      onClick={() => navigation.openThread(thread)}
      onKeyDown={onKeyDown}
      {...(onLongPress === undefined ? {} : press)}
      onPointerDown={(event) => {
        if (onLongPress !== undefined) press.onPointerDown(event);
        else if (event.pointerType === 'mouse') navigation.startDrag?.(thread, event);
      }}
      title={`${thread.title} · ${formatSidebarUpdatedAt(thread.updatedAt)}`}
      type="button"
    >
      <RowStateIcon outcome={row.outcome} state={row.state} />
      <span className={cn('min-w-0 flex-1 truncate', bold && 'font-semibold text-foreground')}>
        {childTitle(thread.title, parentTitle)}
        {row.childCount > 0 && (
          <span className="type-caption ml-1 font-normal text-subtle-foreground">
            · {row.childCount}
          </span>
        )}
      </span>
      {row.task?.round != null && row.outcome === undefined && (
        <span
          aria-label={`Round ${row.task.round.current} of ${row.task.round.max}`}
          className="type-caption shrink-0 text-subtle-foreground tabular-nums"
          data-row-round
          role="img"
        >
          {row.task.round.current}/{row.task.round.max}
        </span>
      )}
      {row.badge !== null && <RowBadge badge={row.badge} />}
      <RowPlace thread={thread} />
      {thread.pullRequest != null && <RowPullRequest pullRequest={thread.pullRequest} />}
      {trailing}
      {thread.availableOnDevice === false ? (
        <FolderX
          aria-label="Not available on this device"
          className="size-3.5 shrink-0 text-subtle-foreground"
          role="img"
        />
      ) : stepsWaiting ? (
        <span
          aria-label="A step waits on you"
          className="size-1.5 shrink-0 rounded-full bg-attention"
          data-row-steps-attention="waiting"
          role="status"
        />
      ) : (
        (thread.unread === true || stepsUnread) &&
        row.waitingSince === null && (
          <span
            aria-label="Unread"
            className="size-1.5 shrink-0 rounded-full bg-foreground"
            data-row-steps-attention={thread.unread === true ? undefined : 'unread'}
            role="status"
          />
        )
      )}
      {age !== null && (
        <span className="type-caption shrink-0 text-subtle-foreground tabular-nums">{age}</span>
      )}
      {pinned && age === null && thread.unread !== true && thread.availableOnDevice !== false && (
        <Pin
          aria-label="Pinned"
          className="size-3 shrink-0 -rotate-45 text-subtle-foreground"
          data-row-pinned
          role="img"
        />
      )}
    </button>
  );
  const Chevron = row.expanded === true ? ChevronDown : ChevronRight;
  return (
    <>
      <div className="group/item relative">
        {onLongPress === undefined ? (
          <ConversationContextMenu actions={actions} title={thread.title}>
            {content}
          </ConversationContextMenu>
        ) : (
          content
        )}
        {moreButton && !renaming && (
          <ConversationActionsButton
            actions={actions}
            className={cn(
              'absolute top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground can-hover:opacity-0 hover:bg-control hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-focus-within/item:opacity-100 group-hover/item:opacity-100 data-[state=open]:opacity-100',
              foldable ? 'right-7' : 'right-1',
            )}
            title={thread.title}
          />
        )}
        {foldable && !renaming && (
          <button
            aria-expanded={row.expanded === true}
            aria-label={
              row.task !== undefined
                ? `${row.expanded === true ? 'Fold' : 'Unfold'} the steps of ${thread.title}`
                : row.expanded === true
                  ? `Fold the conversations of ${thread.title}`
                  : `Unfold the conversations of ${thread.title}`
            }
            className={cn(
              'absolute top-1/2 right-1 flex -translate-y-1/2 items-center justify-center rounded-md text-subtle-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              density === 'desktop' ? 'size-6' : 'size-11',
            )}
            onClick={onToggleChildren}
            type="button"
          >
            <Chevron className="size-3.5" />
          </button>
        )}
      </div>
      {confirmDelete}
    </>
  );
}

const STEP_ROW = {
  desktop: 'h-6 gap-2 rounded-md px-2.5 type-caption',
  drawer: 'min-h-11 gap-3 rounded-xl px-2.5 text-sm',
} as const;

/**
 * A step of a task (one per agent step, loop round or item of its run), compact under its task:
 * its state, its name, what it has unread or since when it waits on you. It opens its
 * conversation.
 */
export function StepRow({
  row,
  depth = 2,
  density = 'desktop',
  parentTitle,
}: {
  row: SidebarRow;
  /** 2 under a task inside an assistant, 1 under a task at the first level. */
  depth?: 1 | 2;
  density?: Density;
  /** Its task's title, not repeated in its own ("· Oct 18"). */
  parentTitle?: string;
}) {
  const navigation = useShellNavigation();
  const { thread } = row;
  const active = thread.id === navigation.activeThreadId;
  const age = row.waitingSince === null ? null : formatAge(row.waitingSince);
  return (
    <button
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full min-w-0 items-center text-left text-muted-foreground transition-colors hover:bg-accent/45 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[unread]:font-semibold data-[unread]:text-foreground',
        STEP_ROW[density],
        depth === 1 && (density === 'desktop' ? 'pl-[42px]' : 'pl-10'),
        depth === 2 && (density === 'desktop' ? 'pl-[62px]' : 'pl-16'),
        active && 'bg-sidebar-accent text-sidebar-accent-foreground',
      )}
      data-sidebar-item
      data-step-row={thread.id}
      data-unread={thread.unread === true || undefined}
      onClick={() => navigation.openThread(thread)}
      title={`${thread.title} · ${formatSidebarUpdatedAt(thread.updatedAt)}`}
      type="button"
    >
      <RowStateIcon className="size-3.5" outcome={row.outcome} state={row.state} />
      <span className="min-w-0 flex-1 truncate">
        {row.stepName === undefined || row.stepName === thread.title
          ? childTitle(thread.title, parentTitle)
          : row.stepName}
      </span>
      {thread.unread === true && age === null && (
        <span
          aria-label="Unread"
          className="size-1.5 shrink-0 rounded-full bg-foreground"
          role="status"
        />
      )}
      {age !== null && <span className="shrink-0 tabular-nums">{age}</span>}
    </button>
  );
}

/** Opens a row's context menu from the keyboard, where a right-click would, below its start. */
function openContextMenu(element: HTMLElement): void {
  const rect = element.getBoundingClientRect();
  element.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: rect.left + 24,
      clientY: rect.bottom,
    }),
  );
}

/** "Weekly dependency update · Oct 18" under "Weekly dependency update" reads "Oct 18". */
export function childTitle(title: string, parentTitle: string | undefined): string {
  if (parentTitle === undefined || !title.startsWith(parentTitle)) return title;
  const rest = title
    .slice(parentTitle.length)
    .replace(/^\s*[·:—–-]\s*/, '')
    .trim();
  return rest === '' || rest.length === title.length - parentTitle.length ? title : rest;
}

function RenameField({
  initial,
  density,
  onDone,
}: {
  initial: string;
  density: Density;
  onDone: (title: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  return (
    <Input
      aria-label="Conversation name"
      className={cn('bg-background', ROW[density])}
      onBlur={() => onDone(value.trim() || null)}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onDone(value.trim() || null);
        else if (event.key === 'Escape') onDone(null);
      }}
      ref={ref}
      value={value}
    />
  );
}

/**
 * An assistant of the sidebar (D1, docs/adr-assistants.md), its channel's row: its colour (its
 * avatar's face on it when it has one), its name, a spinner while its channel runs a turn, an
 * amber dot when it or one of its conversations waits on you, how many of its tasks run ("2
 * running"), bold while unread, and a chevron. The row opens its channel; the chevron shows or
 * hides its work.
 */
export function AssistantRow({
  name,
  color,
  avatar = null,
  busy = false,
  unread = false,
  running,
  waiting,
  expanded,
  active,
  density = 'desktop',
  menu,
  onOpen,
  onToggle,
}: {
  name: string;
  color: string;
  /** The name of the avatar it speaks as, if any: its face. */
  avatar?: string | null;
  /** Its channel runs a turn now. */
  busy?: boolean;
  /** Its channel has something the user has not read. */
  unread?: boolean;
  /** How many of its tasks run now. */
  running: number;
  waiting: boolean;
  expanded: boolean;
  active: boolean;
  density?: Density;
  /** The project's menu (the header's ⋮): on right-click, the menu key and a "⋯" (H8). */
  menu?: readonly RowMenuAction[] | undefined;
  onOpen: () => void;
  onToggle: () => void;
}) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const withMenu = menu !== undefined && menu.length > 0;
  // A pointer that does not right-click, and the keyboard, reach the menu from a "⋯".
  const moreButton = withMenu && density === 'desktop';
  const open = (
    <button
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-w-0 flex-1 items-center text-left font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ROW[density],
        'pr-1',
        unread && 'font-semibold',
      )}
      data-sidebar-item
      data-unread={unread || undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (withMenu && (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey))) {
          event.preventDefault();
          openContextMenu(event.currentTarget);
        }
      }}
      title={`Open ${name}`}
      type="button"
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {avatar === null ? (
          <ProjectDot color={color} />
        ) : (
          <span
            aria-hidden
            className="flex size-4 items-center justify-center rounded-full font-semibold text-foreground text-micro leading-none"
            data-avatar-face
            style={{ backgroundColor: color }}
          >
            {avatar.trim()[0]?.toUpperCase() ?? '?'}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {busy && (
        <LoaderCircle
          aria-label="Running"
          className="size-3.5 shrink-0 animate-spin text-running"
          role="img"
        />
      )}
      {unread && !waiting && (
        <span
          aria-label="Unread"
          className="size-1.5 shrink-0 rounded-full bg-foreground"
          role="status"
        />
      )}
      {waiting && (
        <span
          aria-label="Waits on you"
          className="size-1.5 shrink-0 rounded-full bg-attention"
          role="status"
        />
      )}
      {running > 0 && (
        <span
          className={cn(
            'type-caption shrink-0 font-normal text-subtle-foreground tabular-nums',
            // The "⋯" takes its place on hover or focus.
            moreButton && 'group-focus-within/project:hidden group-hover/project:hidden',
          )}
        >
          {running} running
        </span>
      )}
    </button>
  );
  return (
    <div
      className={cn(
        'group/project flex items-center transition-colors hover:bg-accent/45',
        density === 'desktop' ? 'rounded-lg' : 'rounded-xl',
        active && 'bg-sidebar-accent text-sidebar-accent-foreground',
      )}
    >
      {withMenu ? (
        <ConversationContextMenu actions={menu} title={name}>
          {open}
        </ConversationContextMenu>
      ) : (
        open
      )}
      {moreButton && (
        <ConversationActionsButton
          actions={menu}
          className="hidden size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-focus-within/project:flex group-hover/project:flex data-[state=open]:flex"
          title={name}
        />
      )}
      <button
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Hide' : 'Show'} conversations of ${name}`}
        className={cn(
          'mr-1 flex shrink-0 items-center justify-center rounded-md text-subtle-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          density === 'desktop' ? 'size-6' : 'size-11',
        )}
        onClick={onToggle}
        title={expanded ? 'Hide conversations' : 'Show conversations'}
        type="button"
      >
        <Chevron className="size-3.5" />
      </button>
    </div>
  );
}

/** A section's small heading, with what sits at its right ("+ New"). */
export function SectionHeading({
  label,
  trailing,
  density = 'desktop',
}: {
  label: string;
  trailing?: ReactNode;
  density?: Density;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-2.5 text-subtle-foreground',
        density === 'desktop' ? 'type-caption h-7 pt-2' : 'type-meta h-8 pt-2',
      )}
    >
      <h3 className="font-medium">{label}</h3>
      {trailing}
    </div>
  );
}
